/**
 * Date/Time Utility Functions for Eastern Time Zone
 *
 * These utilities convert between UTC (used by the API) and Eastern Time (ET)
 * for consistent timezone display across the application.
 */

/**
 * Normalizes a UTC timestamp string to ISO format
 * Handles formats like "2025-12-05 00:01:00" or "2025-12-05T00:01:00Z"
 * @param {string} utcString - UTC timestamp string
 * @returns {string} Normalized UTC ISO string
 */
function normalizeUTCString(utcString) {
  if (!utcString) return "";

  // Replace space with 'T' if present (e.g., "2025-12-05 00:01:00" -> "2025-12-05T00:01:00")
  let normalized = utcString.replace(' ', 'T');

  // Add 'Z' suffix if not present
  if (!normalized.endsWith('Z')) {
    normalized += 'Z';
  }

  return normalized;
}

/**
 * Formats a UTC timestamp as a readable Eastern Time string with "ET" suffix
 * @param {string} utcString - UTC ISO string (e.g., "2025-12-10T19:00:00Z" or "2025-12-10 19:00:00")
 * @returns {string} Formatted ET string (e.g., "12/10/2025, 2:00:45 PM ET")
 */
export function formatEasternDateTime(utcString) {
  if (!utcString) return "N/A";

  try {
    // Normalize UTC string to proper ISO format
    const normalized = normalizeUTCString(utcString);
    const date = new Date(normalized);

    // Convert to Eastern Time
    const etString = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    return etString + ' ET';
  } catch (error) {
    console.error('Error formatting Eastern DateTime:', error);
    return "Invalid Date";
  }
}

/**
 * Converts a UTC timestamp to Eastern Time in datetime-local format
 * @param {string} utcString - UTC ISO string (e.g., "2025-12-10T19:00:00Z" or "2025-12-10 19:00:00")
 * @returns {string} ET datetime-local format (e.g., "2025-12-10T14:00")
 */
export function formatEasternForDatetimeLocal(utcString) {
  if (!utcString) return "";

  try {
    // Normalize UTC string to proper ISO format
    const normalized = normalizeUTCString(utcString);
    const date = new Date(normalized);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', utcString);
      return "";
    }

    // Get a formatted string in ET with all components
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);

    // Extract components from the parts array
    const partsMap = {};
    parts.forEach(part => {
      if (part.type !== 'literal') {
        partsMap[part.type] = part.value;
      }
    });

    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    return `${partsMap.year}-${partsMap.month}-${partsMap.day}T${partsMap.hour}:${partsMap.minute}`;
  } catch (error) {
    console.error('Error formatting Eastern for datetime-local:', error);
    return "";
  }
}

/**
 * Converts an Eastern Time datetime-local string to UTC ISO string
 * @param {string} easternDatetimeLocal - ET datetime-local format (e.g., "2025-12-10T14:00")
 * @returns {string} UTC ISO string (e.g., "2025-12-10T19:00:00.000Z")
 */
export function easternToUTC(easternDatetimeLocal) {
  if (!easternDatetimeLocal) return "";

  try {
    // Parse the datetime-local string (format: YYYY-MM-DDTHH:mm)
    const [datePart, timePart] = easternDatetimeLocal.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute] = timePart.split(':');

    // Eastern Time is UTC-5 (EST) or UTC-4 (EDT)
    // We test both offsets and see which one produces the correct ET time when formatted back

    for (let offset = 4; offset <= 5; offset++) {
      // Create UTC time by adding the offset hours
      const utcTime = Date.UTC(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed in Date.UTC
        parseInt(day),
        parseInt(hour) + offset,
        parseInt(minute),
        0,
        0
      );

      const testDate = new Date(utcTime);

      // Format this UTC time back to ET and see if it matches our input
      const formattedBack = formatEasternForDatetimeLocal(testDate.toISOString());

      if (formattedBack === easternDatetimeLocal) {
        return testDate.toISOString();
      }
    }

    // Fallback: if neither offset worked perfectly, use UTC-5 (standard time)
    const utcTime = Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour) + 5,
      parseInt(minute),
      0,
      0
    );

    return new Date(utcTime).toISOString();
  } catch (error) {
    console.error('Error converting Eastern to UTC:', error);
    return "";
  }
}
